import { useState, useEffect, useCallback, useMemo } from "react";
import { getItems, getVendors, createItem, updateItem, deleteItem, api, bulkImportItems, bulkDeleteItems, bulkUpdateCategory, getItemSuggestions, reviewItemSuggestion, getSuggestionCount } from "../lib/api";
import { debounce } from "../lib/utils";
import AppLayout from "../components/AppLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { 
  Plus, Pencil, Trash2, Package, Loader2, Search, Tag, 
  Upload, Download, X, CheckSquare, FolderOpen, AlertCircle, Check, XCircle, Truck
} from "lucide-react";

const UNITS = ["pcs", "kg", "g", "L", "ml", "box", "bag", "pack", "bottle", "can", "dozen"];

export default function ItemsPage() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isBulkCategoryOpen, setIsBulkCategoryOpen] = useState(false);
  const [isBulkDeleteOpen, setIsBulkDeleteOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [bulkCategory, setBulkCategory] = useState("");
  const [newCategoryName, setNewCategoryName] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    unit: "pcs",
    pack_size: "",
    description: "",
    category: "",
    default_vendor_id: "",
  });
  
  // New unit/category input state for edit dialog
  const [showNewUnit, setShowNewUnit] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newUnitInput, setNewUnitInput] = useState("");
  const [newCategoryInput, setNewCategoryInput] = useState("");
  
  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionCount, setSuggestionCount] = useState(0);
  const [showSuggestionsPanel, setShowSuggestionsPanel] = useState(false);
  const [reviewingSuggestion, setReviewingSuggestion] = useState(null);
  const [editedSuggestion, setEditedSuggestion] = useState({});
  
  // Vendor filter state - persisted in localStorage
  const [vendorFilter, setVendorFilter] = useState(() => localStorage.getItem("itemsVendorFilter") || "all");
  
  // Persist vendor filter
  useEffect(() => {
    localStorage.setItem("itemsVendorFilter", vendorFilter);
  }, [vendorFilter]);

  // Debounced search
  const debouncedSetSearch = useCallback(
    debounce((value) => setDebouncedSearch(value), 300),
    []
  );

  useEffect(() => {
    debouncedSetSearch(searchQuery);
  }, [searchQuery, debouncedSetSearch]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const restaurantId = localStorage.getItem("selectedRestaurantId");
      const [itemsRes, vendorsRes, categoriesRes, unitsRes, suggestionsRes, countRes] = await Promise.all([
        getItems(),
        getVendors(),
        api.get(`/categories?restaurant_id=${restaurantId}`),
        api.get(`/units?restaurant_id=${restaurantId}`),
        getItemSuggestions().catch(() => ({ data: [] })),
        getSuggestionCount().catch(() => ({ data: { count: 0 } })),
      ]);
      setItems(itemsRes.data);
      setVendors(vendorsRes.data);
      setCategories(categoriesRes.data);
      setUnits(unitsRes.data || UNITS);
      setSuggestions(suggestionsRes.data || []);
      setSuggestionCount(countRes.data?.count || 0);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setIsLoading(false);
    }
  };

  // Memoized filtered items for performance - sorted by name
  const filteredItems = useMemo(() => {
    let result = items;
    
    // Filter by vendor first
    if (vendorFilter && vendorFilter !== "all") {
      result = result.filter((item) => item.default_vendor_id === vendorFilter);
    }
    
    // Then filter by search
    if (debouncedSearch) {
      const search = debouncedSearch.toLowerCase();
      result = result.filter((item) =>
        item.name.toLowerCase().includes(search) ||
        (item.category && item.category.toLowerCase().includes(search))
      );
    }
    // Sort by name
    return [...result].sort((a, b) => a.name.localeCompare(b.name));
  }, [items, debouncedSearch, vendorFilter]);

  const getVendorName = useCallback((vendorId) => {
    const vendor = vendors.find((v) => v.id === vendorId);
    return vendor?.name || "Unknown";
  }, [vendors]);

  // Selection handlers
  const toggleSelectItem = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === filteredItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(filteredItems.map(item => item.id)));
    }
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  // Dialog handlers
  const openCreateDialog = () => {
    setSelectedItem(null);
    setFormData({
      name: "",
      unit: units[0] || "pcs",
      pack_size: "",
      description: "",
      category: "",
      default_vendor_id: vendors[0]?.id || "",
    });
    setShowNewUnit(false);
    setShowNewCategory(false);
    setNewUnitInput("");
    setNewCategoryInput("");
    setIsDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name || "",
      unit: item.unit || "pcs",
      pack_size: item.pack_size || "",
      description: item.description || "",
      category: item.category || "",
      default_vendor_id: item.default_vendor_id || "",
    });
    setShowNewUnit(false);
    setShowNewCategory(false);
    setNewUnitInput("");
    setNewCategoryInput("");
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (item) => {
    setSelectedItem(item);
    setIsDeleteOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Item name is required");
      return;
    }
    if (!formData.default_vendor_id) {
      toast.error("Please select a vendor");
      return;
    }

    setIsSaving(true);
    try {
      const dataToSend = { ...formData };
      
      // Use new unit if provided
      if (showNewUnit && newUnitInput.trim()) {
        dataToSend.unit = newUnitInput.trim().toLowerCase();
      }
      
      // Use new category if provided
      if (showNewCategory && newCategoryInput.trim()) {
        dataToSend.category = newCategoryInput.trim();
      } else if (!dataToSend.category) {
        dataToSend.category = null;
      }
      
      if (selectedItem) {
        await updateItem(selectedItem.id, dataToSend);
        toast.success("Item updated");
      } else {
        await createItem(dataToSend);
        toast.success("Item created");
      }
      setIsDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save item");
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async (item) => {
    try {
      await updateItem(item.id, { is_active: !item.is_active });
      loadData();
      toast.success(item.is_active ? "Item deactivated" : "Item activated");
    } catch (error) {
      toast.error("Failed to update item");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteItem(selectedItem.id);
      toast.success("Item deleted");
      setIsDeleteOpen(false);
      loadData();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  // Bulk operations
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/items/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "items_template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded");
    } catch (error) {
      toast.error("Failed to download template");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }

    setIsUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append("file", file);
      
      const response = await bulkImportItems(formDataObj);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} items imported successfully`);
      }
      
      if (response.data.skipped > 0) {
        // Show detailed error message
        const errorMsg = response.data.errors?.slice(0, 5).join("\n") || "Check vendor names match exactly";
        toast.error(`${response.data.skipped} items skipped:\n${errorMsg}`, { duration: 8000 });
        
        if (response.data.available_vendors?.length > 0) {
          toast.info(`Available vendors: ${response.data.available_vendors.join(", ")}`, { duration: 10000 });
        }
      }
      
      if (response.data.imported === 0 && response.data.skipped > 0) {
        console.log("Import errors:", response.data.errors);
        console.log("Available vendors:", response.data.available_vendors);
      }
      
      setIsUploadOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import items");
    } finally {
      setIsUploading(false);
      e.target.value = ""; // Reset file input
    }
  };

  const handleBulkDelete = async () => {
    try {
      const response = await bulkDeleteItems(Array.from(selectedItems));
      toast.success(response.data.message);
      setIsBulkDeleteOpen(false);
      clearSelection();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete items");
    }
  };

  const handleBulkCategoryUpdate = async () => {
    const categoryToSet = bulkCategory === "new" ? newCategoryName : (bulkCategory === "none" ? null : bulkCategory);
    
    if (bulkCategory === "new" && !newCategoryName.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    try {
      const response = await bulkUpdateCategory(Array.from(selectedItems), categoryToSet);
      toast.success(response.data.message);
      setIsBulkCategoryOpen(false);
      setBulkCategory("");
      setNewCategoryName("");
      clearSelection();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update category");
    }
  };

  // Suggestion handlers
  const openSuggestionReview = (suggestion) => {
    const item = items.find(i => i.id === suggestion.item_id);
    setReviewingSuggestion(suggestion);
    setEditedSuggestion({
      name: suggestion.suggested_changes.name || item?.name || "",
      pack_size: suggestion.suggested_changes.pack_size || item?.pack_size || "",
      description: suggestion.suggested_changes.description || item?.description || ""
    });
  };

  const handleReviewSuggestion = async (action, suggestion = null) => {
    const targetSuggestion = suggestion || reviewingSuggestion;
    if (!targetSuggestion) return;
    
    try {
      const payload = { action };
      if (action === "edit") {
        payload.edited_values = editedSuggestion;
      }
      
      await reviewItemSuggestion(targetSuggestion.id, payload);
      toast.success(action === "approve" ? "Suggestion approved" : action === "reject" ? "Suggestion rejected" : "Changes applied");
      setReviewingSuggestion(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review suggestion");
    }
  };

  // Check if an item has pending suggestions
  const getItemSuggestion = (itemId) => {
    return suggestions.find(s => s.item_id === itemId);
  };

  const hasSelection = selectedItems.size > 0;

  return (
    <AppLayout title="Items" showBack>
      <div className="p-4 md:p-6 pb-24 max-w-2xl mx-auto">
        {/* Search and Bulk Actions */}
        <div className="space-y-3 mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              type="search"
              placeholder="Search items or categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-mobile"
              data-testid="search-items"
            />
          </div>
          
          {/* Vendor Filter */}
          <div className="flex items-center gap-2">
            <Truck className="w-4 h-4 text-stone-500" />
            <Select value={vendorFilter} onValueChange={setVendorFilter}>
              <SelectTrigger className="h-10 flex-1" data-testid="vendor-filter">
                <SelectValue placeholder="Filter by vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  <div className="flex items-center gap-2">
                    <span>All Vendors</span>
                    <Badge variant="secondary" className="text-xs font-mono">{items.length}</Badge>
                  </div>
                </SelectItem>
                {vendors.map((vendor) => {
                  const count = items.filter(i => i.default_vendor_id === vendor.id).length;
                  return (
                    <SelectItem key={vendor.id} value={vendor.id}>
                      <div className="flex items-center gap-2">
                        <span>{vendor.name}</span>
                        <Badge variant="secondary" className="text-xs font-mono">{count}</Badge>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          
          {/* Suggestions Alert */}
          {suggestionCount > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center relative">
                <AlertCircle className="w-5 h-5 text-amber-600" />
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {suggestionCount}
                </span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-amber-800 text-sm">
                  {suggestionCount} Pending Suggestion{suggestionCount > 1 ? 's' : ''}
                </p>
                <p className="text-xs text-amber-600">Review suggested changes below</p>
              </div>
            </div>
          )}
          
          {/* Import/Export Buttons */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownloadTemplate}
              className="flex-1 h-10"
              data-testid="download-template"
            >
              <Download className="w-4 h-4 mr-2" />
              Template
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsUploadOpen(true)}
              className="flex-1 h-10"
              data-testid="upload-excel"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Excel
            </Button>
          </div>
        </div>

        {/* Bulk Selection Bar */}
        {hasSelection && (
          <div className="bg-kale-50 border border-kale-200 rounded-lg p-3 mb-4 flex items-center justify-between animate-in">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-5 h-5 text-kale-700" />
              <span className="font-medium text-kale-900">{selectedItems.size} selected</span>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsBulkCategoryOpen(true)}
                className="h-8"
                data-testid="bulk-category"
              >
                <FolderOpen className="w-4 h-4 mr-1" />
                Category
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsBulkDeleteOpen(true)}
                className="h-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                data-testid="bulk-delete"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="h-8"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Select All */}
        {filteredItems.length > 0 && (
          <div className="flex items-center gap-2 mb-3 px-1">
            <Checkbox
              checked={selectedItems.size === filteredItems.length && filteredItems.length > 0}
              onCheckedChange={toggleSelectAll}
              data-testid="select-all"
            />
            <span className="text-sm text-stone-500">
              {selectedItems.size === filteredItems.length ? "Deselect all" : "Select all"}
            </span>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-kale-600" />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-12">
            <Package className="w-12 h-12 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 mb-4">
              {searchQuery ? "No items match your search" : "No items yet"}
            </p>
            {!searchQuery && vendors.length > 0 && (
              <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-first-item">
                <Plus className="w-4 h-4 mr-2" /> Add Your First Item
              </Button>
            )}
            {vendors.length === 0 && (
              <p className="text-sm text-stone-400">Add vendors first before creating items</p>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredItems.map((item, index) => {
              const itemSuggestion = getItemSuggestion(item.id);
              return (
              <Card 
                key={item.id} 
                className={`card-hover animate-in stagger-${(index % 4) + 1} ${!item.is_active ? 'opacity-60' : ''} ${selectedItems.has(item.id) ? 'ring-2 ring-kale-500' : ''} ${itemSuggestion ? 'border-amber-300 border-2' : ''}`}
                data-testid={`item-card-${item.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={selectedItems.has(item.id)}
                      onCheckedChange={() => toggleSelectItem(item.id)}
                      className="mt-1"
                      data-testid={`select-item-${item.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <h3 className="font-semibold text-stone-900 truncate">{item.name}</h3>
                        <span className="text-xs font-mono bg-stone-100 px-2 py-0.5 rounded text-stone-600">
                          {item.unit}
                        </span>
                        {item.category && (
                          <Badge variant="secondary" className="bg-kale-100 text-kale-700 text-xs">
                            <Tag className="w-3 h-3 mr-1" />
                            {item.category}
                          </Badge>
                        )}
                        {itemSuggestion && (
                          <Badge 
                            variant="secondary" 
                            className="bg-amber-100 text-amber-700 text-xs cursor-pointer hover:bg-amber-200"
                            onClick={() => openSuggestionReview(itemSuggestion)}
                            data-testid={`suggestion-badge-${item.id}`}
                          >
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Suggested Edit
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-stone-500">
                        {getVendorName(item.default_vendor_id)}
                      </p>
                      {item.pack_size && (
                        <p className="text-xs text-stone-400 mt-1">{item.pack_size}</p>
                      )}
                      {/* Show suggested changes preview */}
                      {itemSuggestion && (
                        <div className="mt-2 p-2 bg-amber-50 rounded-lg text-xs space-y-1">
                          {itemSuggestion.suggested_changes.name && (
                            <p><span className="text-amber-700 font-medium">Name:</span> {itemSuggestion.suggested_changes.name}</p>
                          )}
                          {itemSuggestion.suggested_changes.pack_size && (
                            <p><span className="text-amber-700 font-medium">Pack Size:</span> {itemSuggestion.suggested_changes.pack_size}</p>
                          )}
                          {itemSuggestion.suggested_changes.description && (
                            <p><span className="text-amber-700 font-medium">Description:</span> {itemSuggestion.suggested_changes.description}</p>
                          )}
                          <div className="flex gap-2 mt-2">
                            <Button
                              size="sm"
                              className="h-7 text-xs bg-kale-600 hover:bg-kale-700"
                              onClick={() => handleReviewSuggestion("approve", itemSuggestion)}
                              data-testid={`approve-suggestion-${item.id}`}
                            >
                              <Check className="w-3 h-3 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => openSuggestionReview(itemSuggestion)}
                            >
                              <Pencil className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-xs text-red-600 hover:text-red-700"
                              onClick={() => handleReviewSuggestion("reject", itemSuggestion)}
                              data-testid={`reject-suggestion-${item.id}`}
                            >
                              <XCircle className="w-3 h-3 mr-1" />
                              Reject
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={item.is_active}
                        onCheckedChange={() => handleToggleActive(item)}
                        data-testid={`toggle-item-${item.id}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(item)}
                        className="h-9 w-9 text-stone-500 hover:text-kale-700"
                        data-testid={`edit-item-${item.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDeleteDialog(item)}
                        className="h-9 w-9 text-stone-500 hover:text-red-600"
                        data-testid={`delete-item-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )})}
          </div>
        )}
      </div>

      {/* Sticky Add Button */}
      {vendors.length > 0 && (
        <div className="sticky-bottom">
          <Button onClick={openCreateDialog} className="w-full btn-primary" data-testid="add-item-button">
            <Plus className="w-4 h-4 mr-2" /> Add Item
          </Button>
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedItem ? "Edit Item" : "Add Item"}</DialogTitle>
            <DialogDescription>
              {selectedItem ? "Update item information below." : "Fill in the item details below."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="item-name">Item Name *</Label>
              <Input
                id="item-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Tomatoes"
                className="input-mobile"
                data-testid="item-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="unit">Unit *</Label>
                {!showNewUnit ? (
                  <div className="flex gap-2">
                    <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                      <SelectTrigger className="h-12 flex-1" data-testid="item-unit-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {(units.length > 0 ? units : UNITS).map((unit) => (
                          <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowNewUnit(true)}
                      className="h-12 px-3"
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={newUnitInput}
                      onChange={(e) => setNewUnitInput(e.target.value)}
                      placeholder="Enter new unit"
                      className="input-mobile flex-1"
                      data-testid="new-unit-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => { setShowNewUnit(false); setNewUnitInput(""); }}
                      className="h-12 px-3"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="pack_size">Pack Size</Label>
                <Input
                  id="pack_size"
                  value={formData.pack_size}
                  onChange={(e) => setFormData({ ...formData, pack_size: e.target.value })}
                  placeholder="e.g., 5kg box"
                  className="input-mobile"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              {!showNewCategory ? (
                <div className="flex gap-2">
                  <Select 
                    value={formData.category || "none"} 
                    onValueChange={(v) => setFormData({ ...formData, category: v === "none" ? "" : v })}
                  >
                    <SelectTrigger className="h-12 flex-1" data-testid="item-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Category</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowNewCategory(true)}
                    className="h-12 px-3"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={newCategoryInput}
                    onChange={(e) => setNewCategoryInput(e.target.value)}
                    placeholder="Enter new category"
                    className="input-mobile flex-1"
                    data-testid="new-category-input"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => { setShowNewCategory(false); setNewCategoryInput(""); }}
                    className="h-12 px-3"
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="vendor">Default Vendor *</Label>
              <Select value={formData.default_vendor_id} onValueChange={(v) => setFormData({ ...formData, default_vendor_id: v })}>
                <SelectTrigger className="h-12" data-testid="item-vendor-select">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description"
                className="input-mobile"
              />
            </div>
            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 h-11">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 btn-primary" disabled={isSaving} data-testid="save-item-button">
                {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Upload Excel Dialog */}
      <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Items from Excel</DialogTitle>
            <DialogDescription>
              Upload an Excel file to bulk import items. Download the template first to see the required format.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button
              variant="outline"
              onClick={handleDownloadTemplate}
              className="w-full h-11"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Template
            </Button>
            
            <div className="border-2 border-dashed border-stone-200 rounded-lg p-6 text-center">
              <Upload className="w-8 h-8 text-stone-400 mx-auto mb-2" />
              <p className="text-sm text-stone-500 mb-3">
                Select an Excel file (.xlsx)
              </p>
              <label className="cursor-pointer">
                <Input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={isUploading}
                />
                <Button
                  type="button"
                  variant="secondary"
                  disabled={isUploading}
                  className="pointer-events-none"
                >
                  {isUploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importing...
                    </>
                  ) : (
                    "Choose File"
                  )}
                </Button>
              </label>
            </div>
            
            <p className="text-xs text-stone-400">
              Note: Vendor names in the file must match existing vendors exactly.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Category Dialog */}
      <Dialog open={isBulkCategoryOpen} onOpenChange={setIsBulkCategoryOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Update Category</DialogTitle>
            <DialogDescription>
              Set category for {selectedItems.size} selected item(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Select Category</Label>
              <Select value={bulkCategory} onValueChange={setBulkCategory}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Choose category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Remove Category</SelectItem>
                  <SelectItem value="new">+ Create New Category</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {bulkCategory === "new" && (
              <div className="space-y-2">
                <Label>New Category Name</Label>
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Enter new category name"
                  className="input-mobile"
                />
              </div>
            )}
            
            <DialogFooter className="gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsBulkCategoryOpen(false)} className="flex-1 h-11">
                Cancel
              </Button>
              <Button onClick={handleBulkCategoryUpdate} className="flex-1 btn-primary">
                Update
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Item?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedItem?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={isBulkDeleteOpen} onOpenChange={setIsBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedItems.size} Items?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedItems.size} selected items? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700">
              Delete All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Suggestion Review Dialog */}
      <Dialog open={!!reviewingSuggestion} onOpenChange={(open) => !open && setReviewingSuggestion(null)}>
        <DialogContent className="sm:max-w-md" data-testid="review-suggestion-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              Review Suggestion
            </DialogTitle>
            <DialogDescription>
              Edit values before approving or reject the suggestion.
            </DialogDescription>
          </DialogHeader>
          
          {reviewingSuggestion && (
            <div className="space-y-4">
              <div className="text-xs text-stone-500 bg-stone-50 p-2 rounded">
                Suggested by {reviewingSuggestion.created_by_name} on{" "}
                {new Date(reviewingSuggestion.created_at).toLocaleDateString()}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="review-name">Item Name</Label>
                <Input
                  id="review-name"
                  value={editedSuggestion.name || ""}
                  onChange={(e) => setEditedSuggestion({ ...editedSuggestion, name: e.target.value })}
                  className="input-mobile"
                />
                {reviewingSuggestion.original_values.name !== editedSuggestion.name && (
                  <p className="text-xs text-amber-600">
                    Original: {reviewingSuggestion.original_values.name}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="review-pack">Pack Size</Label>
                <Input
                  id="review-pack"
                  value={editedSuggestion.pack_size || ""}
                  onChange={(e) => setEditedSuggestion({ ...editedSuggestion, pack_size: e.target.value })}
                  className="input-mobile"
                />
                {reviewingSuggestion.original_values.pack_size !== editedSuggestion.pack_size && (
                  <p className="text-xs text-amber-600">
                    Original: {reviewingSuggestion.original_values.pack_size || "(empty)"}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="review-desc">Description</Label>
                <Input
                  id="review-desc"
                  value={editedSuggestion.description || ""}
                  onChange={(e) => setEditedSuggestion({ ...editedSuggestion, description: e.target.value })}
                  className="input-mobile"
                />
                {reviewingSuggestion.original_values.description !== editedSuggestion.description && (
                  <p className="text-xs text-amber-600">
                    Original: {reviewingSuggestion.original_values.description || "(empty)"}
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 pt-4">
            <Button 
              type="button" 
              variant="ghost" 
              onClick={() => handleReviewSuggestion("reject")} 
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <XCircle className="w-4 h-4 mr-1" />
              Reject
            </Button>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setReviewingSuggestion(null)}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleReviewSuggestion("edit")} 
              className="btn-primary"
            >
              <Check className="w-4 h-4 mr-1" />
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
